import React from 'react';
import cn from 'classnames';

import {
  hasKnownDimensions,
  getImageContainerStyle
} from 'widget/utils/getImageStyle';

import MessageMediaFallback from '../MessageMediaFallback';

import styles from './MessageImage.module.scss';

export type MessageImageProps = {
  url: string;
  filename: string;
  size: number;
  height?: number;
  width?: number;
  isFromBot?: boolean;
  onClick?: () => void;
};

const MessageImage: React.FC<MessageImageProps> = ({
  url,
  filename,
  size,
  height,
  width,
  isFromBot = false,
  onClick
}) => {
  const [hasError, setHasError] = React.useState(false);
  const [isLoaded, setIsLoaded] = React.useState(false);

  const handleClick = React.useCallback(() => {
    if (onClick) {
      onClick();
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [onClick, url]);

  const handleError = React.useCallback(() => {
    setHasError(true);
  }, []);

  const handleLoad = React.useCallback(() => {
    setIsLoaded(true);
  }, []);

  if (hasError) {
    return (
      <MessageMediaFallback
        url={url}
        filename={filename}
        size={size}
        variant={isFromBot ? 'default' : 'user'}
      />
    );
  }

  const dimensions = { width, height };
  const knownDimensions = hasKnownDimensions(dimensions);

  const containerStyle = getImageContainerStyle(dimensions);

  return (
    <div className={styles['image']} style={containerStyle}>
      <img
        src={url}
        alt={filename}
        className={cn(styles['image__img'], {
          [styles['image__img_loading']]: !isLoaded
        })}
        onClick={handleClick}
        loading="lazy"
        onError={handleError}
        onLoad={handleLoad}
        width={knownDimensions ? width : undefined}
        height={knownDimensions ? height : undefined}
      />
    </div>
  );
};

export default React.memo(MessageImage);
